// Blog Cache Service - Eliminates loading delays by prefetching and caching all blog data

// ULTRA AGGRESSIVE pre-rendering detection - FIRST THING TO RUN
const IMMEDIATE_PRERENDER_CHECK = (() => {
    // Multiple immediate checks
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
    
    const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isHeadless = userAgent.includes('HeadlessChrome') || 
                      userAgent.includes('puppeteer') || 
                      userAgent.includes('react-snap') ||
                      userAgent.includes('Chrome-Lighthouse');
    
    const hasFlags = typeof window !== 'undefined' && (
        window.__PRERENDER_INJECTED || 
        window.__REACT_SNAP__ || 
        window.__IS_BUILD_TIME__ ||
        window.__HEADLESS_CHROME__ ||
        window.__BLOG_API_DISABLED__
    );
    
    const isHeadlessWindow = typeof window !== 'undefined' && 
                            (window.outerHeight === 0 || window.outerWidth === 0);
    
    return isHeadless || hasFlags || isHeadlessWindow;
})();

// Immediate pre-rendering check
const IS_PRERENDERING = IMMEDIATE_PRERENDER_CHECK || 
                       typeof window === 'undefined' || 
                       typeof navigator === 'undefined' ||
                       (typeof window !== 'undefined' && window.navigator.userAgent.includes('HeadlessChrome')) ||
                       (typeof window !== 'undefined' && window.navigator.userAgent.includes('puppeteer')) ||
                       (typeof window !== 'undefined' && window.__PRERENDER_INJECTED) ||
                       (typeof window !== 'undefined' && window.__REACT_SNAP__);

class BlogCacheService {
    constructor() {
        this.cache = new Map();
        this.isInitialized = false;
        this.initPromise = null;
        this.CACHE_KEY = 'ak_blog_cache';
        this.CACHE_TIMESTAMP_KEY = 'ak_blog_cache_timestamp';
        this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
        
        // Only load from localStorage if not pre-rendering
        if (!IMMEDIATE_PRERENDER_CHECK && !IS_PRERENDERING) {
            this.loadFromLocalStorage();
        }
    }

    // Load cached data from localStorage
    loadFromLocalStorage() {
        // Skip localStorage operations during pre-rendering
        if (IMMEDIATE_PRERENDER_CHECK || IS_PRERENDERING || typeof window === 'undefined') {
            return false;
        }

        try {
            const cachedData = localStorage.getItem(this.CACHE_KEY);
            const cacheTimestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
            
            if (cachedData && cacheTimestamp) {
                const timestamp = parseInt(cacheTimestamp);
                const now = Date.now();
                
                // Check if cache is still valid (within 5 minutes)
                if (now - timestamp < this.CACHE_DURATION) {
                    const blogs = JSON.parse(cachedData);
                    console.log('🚀 INSTANT LOAD: Loading', blogs.length, 'blogs from localStorage cache');
                    
                    // Populate cache
                    blogs.forEach(blog => {
                        this.cache.set(blog.slug, {
                            ...blog,
                            cachedAt: timestamp
                        });
                    });
                    
                    this.isInitialized = true;
                    console.log('✅ Blog cache loaded from localStorage - ZERO FLICKER!');
                    return true;
                } else {
                    console.log('⏰ Cache expired, will refresh from API');
                    this.clearLocalStorage();
                }
            }
        } catch (error) {
            console.warn('⚠️ Failed to load cache from localStorage:', error);
            this.clearLocalStorage();
        }
        return false;
    }

    // Save cache to localStorage
    saveToLocalStorage() {
        // Skip localStorage operations during pre-rendering
        if (IMMEDIATE_PRERENDER_CHECK || IS_PRERENDERING || typeof window === 'undefined') {
            return;
        }

        try {
            const blogs = Array.from(this.cache.values());
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(blogs));
            localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
            console.log('💾 Blog cache saved to localStorage');
        } catch (error) {
            console.warn('⚠️ Failed to save cache to localStorage:', error);
        }
    }

    // Clear localStorage cache
    clearLocalStorage() {
        // Skip localStorage operations during pre-rendering
        if (IMMEDIATE_PRERENDER_CHECK || IS_PRERENDERING || typeof window === 'undefined') {
            return;
        }

        try {
            localStorage.removeItem(this.CACHE_KEY);
            localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
        } catch (error) {
            console.warn('⚠️ Failed to clear localStorage cache:', error);
        }
    }

    // Check if cache is available (for immediate use)
    isCacheAvailable() {
        return this.isInitialized && this.cache.size > 0;
    }

    // Get all cached blogs immediately (for instant loading)
    getCachedBlogs() {
        if (this.isCacheAvailable()) {
            return Array.from(this.cache.values());
        }
        return null;
    }

    // Initialize cache with all blog data
    async initialize(allBlogs) {
        if (this.isInitialized) return;
        
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this._performInitialization(allBlogs);
        return this.initPromise;
    }

    async _performInitialization(allBlogs) {
        try {
            console.log('🚀 Initializing blog cache with', allBlogs.length, 'blogs');
            
            // Cache all blog data by slug for instant access
            allBlogs.forEach(blog => {
                this.cache.set(blog.slug, {
                    ...blog,
                    cachedAt: Date.now()
                });
            });

            this.isInitialized = true;
            
            // Save to localStorage for persistence across page reloads
            this.saveToLocalStorage();
            
            console.log('✅ Blog cache initialized successfully and saved to localStorage');
        } catch (error) {
            console.error('❌ Failed to initialize blog cache:', error);
            throw error;
        }
    }

    // Get blog instantly from cache (no loading delay)
    getBlogBySlug(slug) {
        if (!this.isInitialized) {
            console.warn('⚠️ Blog cache not initialized, returning null');
            return null;
        }

        const cachedBlog = this.cache.get(slug);
        if (cachedBlog) {
            console.log('⚡ Blog retrieved instantly from cache:', slug);
            return cachedBlog;
        }

        console.warn('❌ Blog not found in cache:', slug);
        return null;
    }

    // Check if blog exists in cache
    hasBlog(slug) {
        return this.cache.has(slug);
    }

    // Get all cached blogs
    getAllBlogs() {
        return Array.from(this.cache.values());
    }

    // Update cache with new blog data
    updateBlog(slug, blogData) {
        this.cache.set(slug, {
            ...blogData,
            cachedAt: Date.now()
        });
        console.log('📝 Updated blog in cache:', slug);
    }

    // Add new blog to cache
    addBlog(blogData) {
        this.cache.set(blogData.slug, {
            ...blogData,
            cachedAt: Date.now()
        });
        console.log('➕ Added new blog to cache:', blogData.slug);
    }

    // Clear cache
    clear() {
        this.cache.clear();
        this.isInitialized = false;
        this.initPromise = null;
        this.clearLocalStorage();
        console.log('🗑️ Blog cache cleared (including localStorage)');
    }

    // Get cache statistics
    getStats() {
        return {
            size: this.cache.size,
            isInitialized: this.isInitialized,
            blogs: Array.from(this.cache.keys())
        };
    }

    // Preload critical blog content for even faster access
    async preloadCriticalContent() {
        if (!this.isInitialized) return;

        console.log('🔥 Preloading critical blog content for instant access');
        
        // Get featured blogs and recent blogs for priority caching
        const allBlogs = this.getAllBlogs();
        const featuredBlogs = allBlogs.filter(blog => blog.featured);
        const recentBlogs = allBlogs.slice(0, 10); // First 10 blogs
        
        const criticalBlogs = [...featuredBlogs, ...recentBlogs];
        
        // Ensure these blogs are fully processed and ready
        criticalBlogs.forEach(blog => {
            if (blog.content && typeof blog.content === 'string') {
                // Pre-process markdown content for instant rendering
                try {
                    // This ensures markdown parsing is ready
                    blog._processedContent = blog.content;
                    this.updateBlog(blog.slug, blog);
                } catch (error) {
                    console.warn('⚠️ Failed to preprocess blog:', blog.slug, error);
                }
            }
        });

        console.log('✅ Critical content preloaded for', criticalBlogs.length, 'blogs');
    }
}

// Create singleton instance
const blogCache = new BlogCacheService();

export default blogCache;